import * as React from 'react';
import { FloatingFocusManager, FloatingPortal } from '@floating-ui/react';

import { FixMeLater } from '../../../utils';
import { useForkRef } from '../../../hooks';
import { usePopoverContext } from '../Popover.context';
import { POPOVER_STATIC_STYLES, usePopoverAnimationCssVariables } from '../Popover.styles';

export interface PopoverPanelProps {
    /**
     * The contents of the floating element.
     */
    children: React.ReactNode;

    /**
     * If `true`, focus will be trapped inside the floating element while it is rendered.
     * @default false
     */
    trapFocus?: boolean;
}

export const PopoverPanel: React.ForwardRefExoticComponent<PopoverPanelProps & React.RefAttributes<HTMLElement>> =
    React.forwardRef(function PopoverPanel(
        props: PopoverPanelProps,
        ref: React.ForwardedRef<FixMeLater>
    ): React.JSX.Element | null {
        const { children, trapFocus = false, ...other } = props;

        const {
            context: floatingContext,
            getFloatingProps,
            refs,
            disablePortal,
            transitionStatus,
            placement,
            fullWidth,
            animationStyle,
        } = usePopoverContext();

        const handlePanelRef = useForkRef(ref, refs.setFloating);

        // Prepare CSS variables
        const cssVariables = usePopoverAnimationCssVariables();

        if (!floatingContext.open) {
            return null;
        }

        const baseStyles = floatingContext.floatingStyles;

        const style: React.CSSProperties = fullWidth
            ? {
                  ...baseStyles,
                  left: '0px',
                  right: '0px',
                  position: 'fixed',
                  width: '100vw',
              }
            : baseStyles;

        const panel = (
            // Floating element
            <div
                ref={handlePanelRef}
                style={style}
                css={POPOVER_STATIC_STYLES.floating}
                {...getFloatingProps()}
                {...other}
            >
                {/* Inner element (this animates) */}
                <div
                    style={cssVariables}
                    css={POPOVER_STATIC_STYLES.inner}
                    data-status={transitionStatus}
                    data-placement={placement}
                    data-animation={animationStyle}
                >
                    {children}
                </div>
            </div>
        );

        if (disablePortal) {
            return panel;
        }

        return (
            <FloatingPortal>
                <FloatingFocusManager context={floatingContext} disabled={!trapFocus}>
                    {panel}
                </FloatingFocusManager>
            </FloatingPortal>
        );
    });
